import {useRequiredConfig} from '@domain/shifthandover'
import {FilterDatePicker} from '@hconnect/common/components/dateTimeAndShift/FilterDatePicker'
import {
  StoppageKind,
  shifthandoverStoppagesList,
  DowntimeStoppageCode
} from '@hconnect/common/types'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Done as DoneIcon} from '@mui/icons-material'
import {Button, Box} from '@mui/material'
import {
  AddFilter,
  AllFilterOptions,
  EventTypeFilterDropDown,
  eventAndStoppageCodeTypes,
  StoppageCodeFilterDropDown,
  CategoryFilterDropDown,
  EquipmentFilters,
  FreeTextSearch,
  FunctionsFilterDropDown,
  MaintenanceNotificationFilterDropdown,
  PmNotificationStatusFilterDropDown,
  PrioritiesFilterDropDown,
  ProcessStageFilter,
  RecurrenceFilterDropDown,
  StatusFilterDropDown,
  WorkOrderStatusFilterDropDown,
  WorkOrderTypeFilterDropDown,
  FilterOptions,
  StoppageCodesMap,
  SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE
} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import {intersection, isEmpty} from 'lodash'
import React, {ReactNode, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

interface FilterParams<T> {
  isCollapsible: boolean
  settings: FilterOptions
  options: T[]
  defaultFilters: string[]
}

type Props<T> = {
  onChange: (filterOptions: AllFilterOptions) => void
  settings: AllFilterOptions
  isCollapsible: boolean
  applyFilter: (filter: FilterOptions) => void
  options: T[]
  hideDateFilter?: boolean
  disableTimeOnMultiDay?: boolean
  defaultFilters?: string[]
  additionalActions?: ReactNode
  showDescriptions?: (show: boolean) => void
  plantId: string
}

const getVisibleFilters = <T extends string>({
  isCollapsible,
  settings,
  options,
  defaultFilters
}: FilterParams<T>): string[] => {
  if (isCollapsible) {
    return [...defaultFilters, ...options]
  }
  return Object.keys(settings).reduce(
    (acc, key) => {
      if (settings[key] && defaultFilters.indexOf(key) === -1) {
        acc.push(key)
      }
      return acc
    },
    [...defaultFilters]
  )
}

type FilterButtonProps<T> = {
  isCollapsible: boolean
  settings: FilterOptions
  options: T[]
  defaultFilters: T[]
  applyFilter: (filter: FilterOptions) => void
  onChange: (filterOptions: FilterOptions) => void
  additionalActions?: ReactNode
  t: TFunction
  visibleFilters: string[]
  setFilter: React.Dispatch<React.SetStateAction<string[]>>
}
const getFilterButton = <T extends string>({
  isCollapsible,
  applyFilter,
  options,
  defaultFilters,
  settings,
  additionalActions,
  onChange,
  t,
  visibleFilters,
  setFilter
}: FilterButtonProps<T>) =>
  !isEmpty([...options, ...defaultFilters]) && (
    <>
      {!isCollapsible ? (
        !isEmpty(options) ? (
          <>
            <AddFilter<T>
              data-test-id={'events-filter-bar-add-filter'}
              options={options}
              translate={t}
              onChange={(value: string[] = []) => {
                return setFilter((prevFilters) => {
                  const intersection = prevFilters.filter((x: string) => !value.includes(x))
                  const filterName = intersection[0] as keyof FilterOptions
                  onChange({...settings, [filterName]: undefined})
                  return value
                })
              }}
              values={visibleFilters}
            />
            {additionalActions}
          </>
        ) : (
          <></>
        )
      ) : (
        <Box m={1}>
          <Button
            fullWidth
            data-test-id={'events-filter-apply-filter'}
            onClick={() => applyFilter(settings)}
            variant="contained"
            color="primary"
            startIcon={<DoneIcon />}
          >
            {t('shiftEvent.label.applyFilter')}
          </Button>
        </Box>
      )}
    </>
  )

const renderStatusFilters = (
  visibleFilters: string[],
  onChange: (filterOptions: AllFilterOptions) => void,
  settings: AllFilterOptions,
  removeFilter: (filterName: string) => void,
  isCollapsible: boolean,
  selectAll: (filterName: string, options: string[]) => void
) => {
  return (
    <>
      {visibleFilters.includes('status') && (
        <StatusFilterDropDown
          data-test-id={'events-filter-bar-status'}
          onChange={(value) => onChange({...settings, status: value})}
          value={settings.status}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {visibleFilters.includes('pmNotificationStatus') && (
        <PmNotificationStatusFilterDropDown
          data-test-id={'pm-notification-filter-bar-status'}
          onChange={(value) => onChange({...settings, pmNotificationStatus: value})}
          value={settings.pmNotificationStatus}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {visibleFilters.includes('workOrderStatus') && (
        <WorkOrderStatusFilterDropDown
          data-test-id={'work-order-filter-bar-status'}
          onChange={(value) => onChange({...settings, workOrderStatus: value})}
          value={settings.workOrderStatus}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
    </>
  )
}

export function Filter<T extends string>(props: Props<T>) {
  const {
    onChange,
    settings,
    isCollapsible,
    additionalActions,
    applyFilter,
    options,
    defaultFilters = [],
    plantId
  } = props
  const [visibleFilters, setFilter] = useState<string[]>(
    getVisibleFilters({isCollapsible, settings, options, defaultFilters})
  )
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {t: filterT} = useTranslation(SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE)
  const {shifts, kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes} =
    useRequiredConfig(plantId)

  const removeFilter = (filterName: string) => {
    onChange({...settings, [filterName]: undefined})
    setFilter((prevFilters) => prevFilters.filter((pf) => pf !== filterName))
  }

  const selectAll = (filterName: string, options) => {
    onChange({...settings, [filterName]: options})
  }

  const allCodesMap = useMemo<StoppageCodesMap<DowntimeStoppageCode>>(
    () => ({
      kiln: kilnStoppageCodes,
      rawMill: rawMillStoppageCodes,
      cementMill: finishMillStoppageCodes
    }),
    [kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes]
  )

  const shouldShowStoppedMainEquipment = useCommonFeatureFlag(FeatureFlagKey.StoppedMainEquipment)
  const allowedOptions = shouldShowStoppedMainEquipment
    ? props.options
    : props.options.filter((option) => option !== 'stoppedMainEquipment')

  return (
    <Box
      data-test-id={'events-filter-bar-filter-options'}
      display={'flex'}
      sx={
        isCollapsible
          ? {
              flexDirection: 'column',
              paddingTop: '30px'
            }
          : {
              flexWrap: 'wrap',
              alignItems: 'center'
            }
      }
    >
      {visibleFilters.includes('timeRange') && (
        <FilterDatePicker
          translate={t}
          language={language}
          shifts={shifts}
          timezone={timezone}
          defaultTimeRange={'currentShift'}
          plantNow={getPlantNow()}
          onChange={(value) => {
            onChange({...settings, timeRange: value})
          }}
          value={settings.timeRange}
          disableTimeOnMultiDay={props.disableTimeOnMultiDay}
        />
      )}
      {visibleFilters.includes('processStage') && (
        <ProcessStageFilter
          onChange={(processStage) => onChange({...settings, processStage})}
          removeFilter={removeFilter}
          value={settings.processStage}
          selectAll={selectAll}
          isCollapsible={isCollapsible}
          plantId={plantId}
        />
      )}
      <EquipmentFilters
        visibleFilters={visibleFilters}
        removeFilter={removeFilter}
        plantId={plantId}
        equipment={settings.equipment}
        onEquipmentChange={(equipment) => onChange({...settings, equipment})}
        mainEquipment={settings.mainEquipment}
        onMainEquipmentChange={(mainEquipment) => onChange({...settings, mainEquipment})}
        functionalEquipment={settings.functionalEquipment}
        onFunctionalEquipmentChange={(functionalEquipment) =>
          onChange({...settings, functionalEquipment})
        }
        stoppedMainEquipment={settings.stoppedMainEquipment}
        onStoppedMainEquipmentChange={(stoppedMainEquipment) =>
          onChange({...settings, stoppedMainEquipment})
        }
      />
      {visibleFilters.includes('eventType') && (
        <EventTypeFilterDropDown
          data-test-id={'events-filter-bar-eventType'}
          onChange={(value) => onChange({...settings, eventType: value, stoppageCode: undefined})}
          value={settings.eventType}
          removeFilter={removeFilter}
          label={t('shiftEvent.label.eventType')}
          isCollapsible={isCollapsible}
          options={eventAndStoppageCodeTypes}
          name="eventType"
          selectAll={(options) => {
            onChange({...settings, eventType: options, stoppageCode: undefined})
          }}
        />
      )}

      {visibleFilters.includes('category') && (
        <CategoryFilterDropDown
          data-test-id={'events-filter-bar-category'}
          onChange={(value) => onChange({...settings, category: value})}
          value={settings.category}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}

      {visibleFilters.includes('priority') && (
        <PrioritiesFilterDropDown
          data-test-id={'events-filter-bar-priority'}
          onChange={(value) => onChange({...settings, priority: value})}
          value={settings.priority}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {visibleFilters.includes('changeFunctions') && (
        <FunctionsFilterDropDown
          data-test-id={'events-filter-bar-changeFunctions'}
          onChange={(value) => onChange({...settings, changeFunctions: value})}
          value={settings.changeFunctions}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {renderStatusFilters(
        visibleFilters,
        onChange,
        settings,
        removeFilter,
        isCollapsible,
        selectAll
      )}
      {visibleFilters.includes('repetition') && (
        <RecurrenceFilterDropDown
          data-test-id={'events-filter-bar-recurrence'}
          onChange={(value) => onChange({...settings, repetition: value})}
          value={settings.repetition}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {visibleFilters.includes('stoppageCode') && (
        <StoppageCodeFilterDropDown
          data-test-id={'events-filter-bar-stoppageCode'}
          onChange={(value) => {
            onChange({...settings, stoppageCode: value})
          }}
          value={settings.stoppageCode}
          stoppageKinds={
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            intersection(settings.eventType, shifthandoverStoppagesList) as StoppageKind[]
          }
          stoppageCodesMap={allCodesMap}
        />
      )}
      {visibleFilters.includes('freeText') && (
        <FreeTextSearch
          data-test-id={'events-filter-bar-freeText'}
          onChange={(value) => {
            onChange({...settings, freeText: value})
          }}
          value={settings.freeText}
          showDescriptions={props.showDescriptions}
        />
      )}
      {visibleFilters.includes('maintenanceNotificationExists') && (
        <MaintenanceNotificationFilterDropdown
          data-test-id={'events-filter-bar-maintenanceNotificationExists'}
          id={'events-filter-bar-maintenanceNotificationExists'}
          onChange={(value) =>
            onChange({
              ...settings,
              maintenanceNotificationExists: value
            })
          }
          value={settings.maintenanceNotificationExists}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
        />
      )}

      {visibleFilters.includes('orderType') && (
        <WorkOrderTypeFilterDropDown
          data-test-id="work-order-filter-bar-type"
          onChange={(value) => onChange({...settings, orderType: value})}
          value={settings.orderType}
          removeFilter={removeFilter}
          isCollapsible={isCollapsible}
          selectAll={selectAll}
        />
      )}
      {getFilterButton({
        isCollapsible,
        applyFilter,
        options: allowedOptions,
        defaultFilters,
        settings,
        additionalActions,
        onChange,
        t: filterT,
        visibleFilters,
        setFilter
      })}
    </Box>
  )
}
